// react
import { useMemo, useState } from 'react';
import styled, { css } from 'styled-components';

// component
import {
  ChipProps,
  Icon,
  LoadingTransition,
  QuickResult,
  Search,
  Title,
  TooltipOnboarding,
} from '@/components/atoms';
import { ChipGroup } from '@/components/molecules';
import { ArticleFiltered, ArticleSummary } from '@/components/organisms';

// store
import { Article, ArticleCategory } from '@karehero/models';

const onboardingSteps = 2;

type DummyArticle = {
  title: string;
};

type DummyArticleCategory = {
  title: string;
  articles: DummyArticle[];
};

/**
 * ArticleOverview is a page that displays all of the content that available
 * to the user.
 */
export const ArticleOverview = ({
  library,
  bookmarkedArticles,
  onBookmark,
  isLoading,
  searchValue,
  onSearchChange,
  isSearchLoading,
  quickResultsHeader,
  quickResults,
  filterChips,
  filteredArticles,
  isFilteredLoading,
  isOnboarding,
  toBookmarks,
  onOnboardingComplete,
  isEasterEgg,
}: ArticleOverviewProps) => {
  // state
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [onboardingIndex, setOnboardingIndex] = useState(isOnboarding ? 0 : -1);

  const dummyArticles = useMemo(() => {
    let dummyArticles: DummyArticle[] = [];
    for (let i = 0; i < 32; i++) {
      dummyArticles.push({ title: `Article ${i}` });
    }
    return dummyArticles;
  }, []);

  // memos
  const dummyLibrary = useMemo(() => {
    let dummyLibrary: DummyArticleCategory[] = [];
    for (let i = 0; i < 4; i++) {
      dummyLibrary.push({
        title: `Category ${i}`,
        articles: dummyArticles,
      });
    }
    return dummyLibrary;
  }, [dummyArticles]);

  return (
    <StyledArticleOverview>
      <TooltipOnboarding
        index={0}
        title='Searching'
        content='Explore the library by using the search bar and filters'
        total={onboardingSteps}
        current={onboardingIndex}
        setCurrent={setOnboardingIndex}
        onComplete={onOnboardingComplete}
      >
        <MainHeader>
          <Header>
            <Title isNoMargin>Library</Title>
            <PageHeader>
              <HeaderLeft>
                <Description>
                  This is where you’ll find useful resources, interesting read
                  and useful videos.
                </Description>
              </HeaderLeft>
              <HeaderRight>
                <Search
                  isOpen={isSearchOpen}
                  setIsOpen={setIsSearchOpen}
                  value={searchValue}
                  isLoading={isSearchLoading}
                  onChange={onSearchChange}
                  quickResultsHeader={quickResultsHeader}
                  quickResults={quickResults}
                  isEasterEgg={isEasterEgg}
                />
              </HeaderRight>
            </PageHeader>
          </Header>
          <FilterWrapper>
            <ChipGroup
              chips={[
                ...(filterChips ? filterChips : []),
                {
                  label: 'Bookmarks',
                  icon: 'bookmark',
                  to: toBookmarks || '/',
                },
              ]}
            />
          </FilterWrapper>
        </MainHeader>
      </TooltipOnboarding>
      <LoadingTransition
        isLoading={isLoading}
        loading={
          <Content>
            {isFilteredLoading ? (
              <ArticleFiltered articles={dummyArticles} isLoading />
            ) : (
              <ArticleSummary library={dummyLibrary} isLoading />
            )}
          </Content>
        }
        content={
          <>
            {filteredArticles ? (
              <Content>
                <ArticleFiltered
                  articles={filteredArticles}
                  bookmarkedArticles={bookmarkedArticles}
                  onBookmark={onBookmark}
                  isLoading={false}
                />
              </Content>
            ) : (
              <TooltipOnboarding
                index={1}
                title='Discover'
                content='Find articles that may be of interest to you'
                total={onboardingSteps}
                current={onboardingIndex}
                setCurrent={setOnboardingIndex}
                onComplete={onOnboardingComplete}
              >
                <Content>
                  <ArticleSummary
                    library={library}
                    bookmarkedArticles={bookmarkedArticles}
                    onBookmark={onBookmark}
                    isLoading={false}
                  />
                </Content>
              </TooltipOnboarding>
            )}
          </>
        }
      />
    </StyledArticleOverview>
  );
};

const StyledArticleOverview = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[36]};
  padding-bottom: ${(props) => props.theme.spacing[32]};
  padding-left: ${(props) => props.theme.spacing[4]};
  padding-right: ${(props) => props.theme.spacing[4]};
`;

const MainHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[36]};
  width: 100%;
`;

const Description = styled.div`
  font: ${(props) => props.theme.typography.body.md.regular};
  color: ${(props) => props.theme.color.neutral[80]};
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
`;

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[16]};

  ${(props) =>
    props.theme.breakpoint.md(css`
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
    `)}
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
`;

const HeaderRight = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`;

const Content = styled.div`
  display: flex;
  gap: ${(props) => props.theme.spacing[32]};
  flex-wrap: wrap;
`;

const FilterWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${(props) => props.theme.spacing[16]};
  max-width: 100%;
`;

interface ArticleOverviewProps {
  library?: ArticleCategory[];
  bookmarkedArticles?: Article[];
  onBookmark?: (article: Partial<Article>) => void;
  isLoading?: boolean;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  isSearchLoading?: boolean;
  quickResultsHeader?: string;
  quickResults?: QuickResult[];
  filterChips?: ChipProps[];
  filteredArticles?: Article[];
  isFilteredLoading?: boolean;
  isOnboarding?: boolean;
  onOnboardingComplete?: () => void;
  toBookmarks?: string;
  isEasterEgg?: boolean;
}

ArticleOverview.displayName = 'ArticleOverview';

export default ArticleOverview;
